import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useAppSettings } from '../hooks/useAppSettings'
import { formatRelativeTime as fmtRelative, formatDate as fmtDate, formatTime as fmtTime } from '../utils/time'
import PaywallModal from '../components/PaywallModal'
import { collection, query, where, orderBy, limit, getDocs, doc, getDoc } from 'firebase/firestore'
import { db } from '../utils/firebase'
import {
  MessageSquare,
  Megaphone,
  Users,
  ArrowRight,
  Wallet,
  Home,
  Calendar,
} from 'lucide-react'
import './DashboardPage.css'

function DashboardPage() {
  const { userProfile, isSubscriptionActive } = useAuth()
  const { settings } = useAppSettings()
  const isAdmin = userProfile?.isAdmin || userProfile?.isBoardMember || userProfile?.isSuperAdmin
  const [stats, setStats] = useState({
    totalMembers: 0,
    recentDiscussions: 0,
    announcements: 0,
    unreadMessages: 0
  })
  const [recentDiscussions, setRecentDiscussions] = useState([])
  const [recentAnnouncements, setRecentAnnouncements] = useState([])
  const [recentResidents, setRecentResidents] = useState([])
  const [upcomingEvents, setUpcomingEvents] = useState([])
  // { email → profilePhotoURL } for the neighborhood. Discussions /
  // announcements store `authorId` as email; the tile rows use this
  // map to render real avatars instead of initials.
  const [authorPhotos, setAuthorPhotos] = useState({})
  const [loading, setLoading] = useState(true)
  const [neighborhoodName, setNeighborhoodName] = useState('')
  const [neighborhoodLogo, setNeighborhoodLogo] = useState('')

  useEffect(() => {
    if (userProfile?.neighborhoodId) {
      loadDashboardData()
    } else if (userProfile !== null) {
      setLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const neighborhoodId = userProfile.neighborhoodId

      // Load neighborhood name + logo
      try {
        const neighborhoodDoc = await getDoc(doc(db, 'neighborhoods', neighborhoodId))
        if (neighborhoodDoc.exists()) {
          const data = neighborhoodDoc.data()
          setNeighborhoodName(data.name || neighborhoodId)
          setNeighborhoodLogo(typeof data.logoUrl === 'string' ? data.logoUrl : '')
        }
      } catch (e) {
        console.error('Error loading neighborhood name:', e)
      }

      // Load member count + a small sample for the Directory tile
      const usersQuery = query(
        collection(db, 'users'),
        where('neighborhoodId', '==', neighborhoodId)
      )
      const usersSnapshot = await getDocs(usersQuery)
      const memberCount = usersSnapshot.size
      const allUsers = usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }))
      const residentsSample = allUsers
        .filter(u => u.showInDirectory !== false)
        .slice(0, 3)

      // Build the author-photo lookup for the discussions/announcements
      // tile rows. Keyed by email since that's what those docs use as
      // authorId.
      const photoMap = {}
      allUsers.forEach(u => {
        if (u.email && u.profilePhotoURL) photoMap[u.email] = u.profilePhotoURL
      })
      setAuthorPhotos(photoMap)

      // Load recent discussions
      const discussionsQuery = query(
        collection(db, 'discussions'),
        where('neighborhoodId', '==', neighborhoodId),
        orderBy('lastActivity', 'desc'),
        limit(3)
      )
      const discussionsSnapshot = await getDocs(discussionsQuery)
      const discussions = discussionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdDate: doc.data().createdDate?.toDate() || new Date(),
        lastActivity: doc.data().lastActivity?.toDate() || new Date()
      }))

      // Load recent announcements
      const announcementsQuery = query(
        collection(db, 'announcements'),
        where('neighborhoodId', '==', neighborhoodId),
        orderBy('createdDate', 'desc'),
        limit(3)
      )
      const announcementsSnapshot = await getDocs(announcementsQuery)
      const announcements = announcementsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdDate: doc.data().createdDate?.toDate() || new Date()
      }))

      // Load unread message count by summing unreadCount[uid] across every
      // conversation this user participates in. Mirrors the same field
      // MessagesPage uses to render per-conversation badges, so the
      // dashboard counter and the in-app badges always agree.
      let unreadMessages = 0
      try {
        const conversationsQuery = query(
          collection(db, 'conversations'),
          where('participants', 'array-contains', userProfile.uid)
        )
        const conversationsSnapshot = await getDocs(conversationsQuery)
        conversationsSnapshot.docs.forEach(d => {
          const data = d.data()
          const count = data.unreadCount?.[userProfile.uid] || 0
          unreadMessages += count
        })
      } catch (msgErr) {
        console.error('Error loading unread message count:', msgErr)
      }

      setStats({
        totalMembers: memberCount,
        recentDiscussions: discussions.length,
        announcements: announcements.length,
        unreadMessages
      })
      setRecentDiscussions(discussions)
      setRecentAnnouncements(announcements)
      setRecentResidents(residentsSample)

      // Upcoming events (next 3 whose startAt >= now). Errors don't
      // block the dashboard — the tile just renders empty.
      try {
        const eventsSnap = await getDocs(query(
          collection(db, 'events'),
          where('neighborhoodId', '==', neighborhoodId),
          orderBy('startAt', 'asc')
        ))
        const now = new Date()
        const events = eventsSnap.docs
          .map(d => ({ id: d.id, ...d.data(), startAt: d.data().startAt?.toDate?.() || null }))
          .filter(e => e.startAt && e.startAt >= now)
          .slice(0, 3)
        setUpcomingEvents(events)
      } catch (evErr) {
        console.warn('Could not load upcoming events:', evErr)
        setUpcomingEvents([])
      }

    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatRelativeTime = (date) => fmtRelative(date, settings)

  const initial = (name) => (name || '?').trim().charAt(0).toUpperCase()

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p className="muted">Loading dashboard…</p>
      </div>
    )
  }

  const locationLabel = neighborhoodName || userProfile?.neighborhoodId || 'your neighborhood'
  const firstName = userProfile?.firstName || 'Neighbor'
  const memberCountLabel = `${stats.totalMembers} ${stats.totalMembers === 1 ? 'resident' : 'residents'}`

  return (
    <div className="dashboard">
      {!isSubscriptionActive() && <PaywallModal isAdmin={isAdmin} />}

      {/* Page header — matches the vocabulary of the other redesigned pages. */}
      <header className="dashboard-header">
        <div className="dashboard-header-text">
          <h1>Welcome back, {firstName}</h1>
          <p className="muted">Here’s what’s new in {locationLabel} today.</p>
        </div>
      </header>

      {/* Hero card — neighborhood logo left, headline + count center, CTA right. */}
      <section className="card dashboard-hero">
        <div className="avatar xl square dashboard-hero-logo" aria-hidden="true">
          {neighborhoodLogo ? (
            <img src={neighborhoodLogo} alt="" />
          ) : (
            <Home size={36} />
          )}
        </div>
        <div className="dashboard-hero-text">
          <h2>{locationLabel}</h2>
          <p className="muted">
            {memberCountLabel} · {stats.unreadMessages > 0
              ? `${stats.unreadMessages} unread ${stats.unreadMessages === 1 ? 'message' : 'messages'}`
              : 'You’re all caught up'}
          </p>
        </div>
      </section>

      {/* Section tiles — responsive grid of compact cards. */}
      <div className="dashboard-tiles">
        {/* Latest Discussions */}
        <Link to="/discussions" className="card interactive dashboard-tile">
          <header className="dashboard-tile-header">
            <div className="dashboard-tile-title">
              <MessageSquare size={18} />
              <h3>Latest discussions</h3>
            </div>
            <span className="badge">{recentDiscussions.length}</span>
          </header>
          <ul className="dashboard-tile-list">
            {recentDiscussions.length === 0 ? (
              <li className="dashboard-tile-empty muted">
                No discussions yet — start the first one.
              </li>
            ) : (
              recentDiscussions.map(d => (
                <li key={d.id} className="dashboard-tile-row">
                  <div className="avatar sm" aria-hidden="true">
                    {authorPhotos[d.authorId] ? (
                      <img
                        src={authorPhotos[d.authorId]}
                        alt=""
                        onError={(e) => { e.currentTarget.style.display = 'none' }}
                      />
                    ) : (
                      initial(d.authorFirstName || d.authorName)
                    )}
                  </div>
                  <div className="dashboard-tile-row-text">
                    <span className="dashboard-tile-row-title">{d.title}</span>
                    <span className="muted dashboard-tile-row-meta">
                      {d.authorFirstName || 'Anonymous'} · {formatRelativeTime(d.lastActivity)}
                    </span>
                  </div>
                </li>
              ))
            )}
          </ul>
          <footer className="dashboard-tile-footer">
            <span>See all discussions</span>
            <ArrowRight size={16} />
          </footer>
        </Link>

        {/* Recent Announcements */}
        <Link to="/announcements" className="card interactive dashboard-tile">
          <header className="dashboard-tile-header">
            <div className="dashboard-tile-title">
              <Megaphone size={18} />
              <h3>Recent announcements</h3>
            </div>
            <span className="badge">{recentAnnouncements.length}</span>
          </header>
          <ul className="dashboard-tile-list">
            {recentAnnouncements.length === 0 ? (
              <li className="dashboard-tile-empty muted">
                No announcements yet.
              </li>
            ) : (
              recentAnnouncements.map(a => (
                <li key={a.id} className="dashboard-tile-row">
                  <div className="avatar sm" aria-hidden="true">
                    {authorPhotos[a.authorId] ? (
                      <img
                        src={authorPhotos[a.authorId]}
                        alt=""
                        onError={(e) => { e.currentTarget.style.display = 'none' }}
                      />
                    ) : (
                      initial(a.authorName)
                    )}
                  </div>
                  <div className="dashboard-tile-row-text">
                    <span className="dashboard-tile-row-title">
                      {a.title}
                      {a.isPinned && <span className="badge dashboard-pin-badge">Pinned</span>}
                    </span>
                    <span className="muted dashboard-tile-row-meta">
                      {a.authorName || 'Board'} · {formatRelativeTime(a.createdDate)}
                    </span>
                  </div>
                </li>
              ))
            )}
          </ul>
          <footer className="dashboard-tile-footer">
            <span>See all announcements</span>
            <ArrowRight size={16} />
          </footer>
        </Link>

        {/* Upcoming Events */}
        <Link to="/events" className="card interactive dashboard-tile">
          <header className="dashboard-tile-header">
            <div className="dashboard-tile-title">
              <Calendar size={18} />
              <h3>Upcoming events</h3>
            </div>
            <span className="badge">{upcomingEvents.length}</span>
          </header>
          <ul className="dashboard-tile-list">
            {upcomingEvents.length === 0 ? (
              <li className="dashboard-tile-empty muted">
                No upcoming events.
              </li>
            ) : (
              upcomingEvents.map(e => {
                const monthLbl = e.startAt
                  ? fmtDate(e.startAt, settings, { month: 'short', day: undefined, year: undefined }).toUpperCase()
                  : '—'
                const dayLbl = e.startAt ? e.startAt.getDate() : '?'
                const dayName = e.startAt
                  ? fmtDate(e.startAt, settings, { weekday: 'short', month: undefined, day: undefined, year: undefined })
                  : 'TBD'
                const timeLbl = e.startAt ? fmtTime(e.startAt, settings) : ''
                return (
                  <li key={e.id} className="dashboard-tile-row">
                    <div className="dashboard-event-datechip" aria-hidden="true">
                      <span className="dashboard-event-datechip-month">{monthLbl}</span>
                      <span className="dashboard-event-datechip-day">{dayLbl}</span>
                    </div>
                    <div className="dashboard-tile-row-text">
                      <span className="dashboard-tile-row-title">{e.title || 'Untitled event'}</span>
                      <span className="muted dashboard-tile-row-meta">
                        {dayName}{timeLbl && ` · ${timeLbl}`}
                        {e.location && ` · ${e.location}`}
                      </span>
                    </div>
                  </li>
                )
              })
            )}
          </ul>
          <footer className="dashboard-tile-footer">
            <span>See all events</span>
            <ArrowRight size={16} />
          </footer>
        </Link>

        {/* Upcoming Payments — admins only */}
        {isAdmin && (
          <Link to="/admin" className="card interactive dashboard-tile">
            <header className="dashboard-tile-header">
              <div className="dashboard-tile-title">
                <Wallet size={18} />
                <h3>Upcoming payments</h3>
              </div>
              <span className="badge">Admin</span>
            </header>
            <ul className="dashboard-tile-list">
              <li className="dashboard-tile-row">
                <div className="avatar sm" aria-hidden="true"><Wallet size={14} /></div>
                <div className="dashboard-tile-row-text">
                  <span className="dashboard-tile-row-title">HOA fee tracker</span>
                  <span className="muted dashboard-tile-row-meta">
                    Review who’s paid and send reminders.
                  </span>
                </div>
              </li>
              <li className="dashboard-tile-row">
                <div className="avatar sm" aria-hidden="true"><Users size={14} /></div>
                <div className="dashboard-tile-row-text">
                  <span className="dashboard-tile-row-title">{memberCountLabel}</span>
                  <span className="muted dashboard-tile-row-meta">
                    Tracked in {locationLabel}
                  </span>
                </div>
              </li>
            </ul>
            <footer className="dashboard-tile-footer">
              <span>Open admin tools</span>
              <ArrowRight size={16} />
            </footer>
          </Link>
        )}

        {/* Directory shortcut */}
        <Link to="/directory" className="card interactive dashboard-tile">
          <header className="dashboard-tile-header">
            <div className="dashboard-tile-title">
              <Users size={18} />
              <h3>Directory</h3>
            </div>
            <span className="badge">{stats.totalMembers}</span>
          </header>
          <ul className="dashboard-tile-list">
            {recentResidents.length === 0 ? (
              <li className="dashboard-tile-empty muted">
                No neighbors listed yet.
              </li>
            ) : (
              recentResidents.map(r => (
                <li key={r.id} className="dashboard-tile-row">
                  <div className="avatar sm" aria-hidden="true">
                    {r.profilePhotoURL ? (
                      <img
                        src={r.profilePhotoURL}
                        alt=""
                        onError={(e) => { e.currentTarget.style.display = 'none' }}
                      />
                    ) : (
                      initial(r.name)
                    )}
                  </div>
                  <div className="dashboard-tile-row-text">
                    <span className="dashboard-tile-row-title">{r.name || 'Neighbor'}</span>
                    <span className="muted dashboard-tile-row-meta">
                      {r.address ? r.address.split(',')[0] : 'Resident'}
                    </span>
                  </div>
                </li>
              ))
            )}
          </ul>
          <footer className="dashboard-tile-footer">
            <span>Browse the directory</span>
            <ArrowRight size={16} />
          </footer>
        </Link>
      </div>
    </div>
  )
}

export default DashboardPage
